import { useId, type CSSProperties } from 'react';
import { IDIOMAS_SITE } from '../../../utils/idiomaMoedaSite';
import { BANDEIRA_IDIOMA, NOME_IDIOMA, trocarIdioma, useIdiomaSite } from '../../../utils/idiomaSite';
import styles from './IdiomasMenu.module.css';

// LINHA "IDIOMA" DOS MENUS (22/10/2026) — opções com bandeira + nome (Português, English, Español,
// Français, Italiano, Deutsch = IDIOMAS_SITE), a ativa destacada. Entra no painel/gaveta que abre no
// "Menu"/☰ de cada modelo, sempre atrás de `temSeletorIdiomas(site)` no ponto de chamada.
// O visual é do MODELO: quem chama passa as classes do próprio modelo (bloco, rótulo, link) e/ou as
// variáveis --idm-* (acento, raio, borda); sem classe, cada opção é uma pílula que herda cor e fonte do
// menu. Os nomes dos idiomas não são traduzidos (`notranslate`); o rótulo "Idioma" é.
// Trocar = utils/idiomaSite.trocarIdioma (widget escondido + moeda + `?lang=`).

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(' ');

export type PropsIdiomasMenu = {
    /** Rótulo da linha ("Idioma"); `null` = sem rótulo (ex.: dentro de um menu suspenso). */
    titulo?: string | null;
    classeBloco?: string;
    classeTitulo?: string;
    estiloTitulo?: CSSProperties;
    /** Classe do modelo para cada opção (senão, a pílula padrão) e para a ativa. */
    classeOpcao?: string;
    classeOpcaoAtiva?: string;
    /** Opções em coluna (menus suspensos e listas do modelo). */
    vertical?: boolean;
    /** Variáveis --idm-* (acento, raio, borda…) e o que mais o modelo precisar no bloco. */
    estilo?: CSSProperties;
    /** Depois de escolher (ex.: fechar o menu). */
    aoEscolher?: () => void;
    /** Mostrar só numa faixa de largura — para o modelo que muda a linha de lugar quando vira 1 coluna
     *  (no alto do menu no celular, na coluna lateral no computador). Larguras = breakpoints do modelo. */
    ocultarAte?: 768 | 1023;
    ocultarAcima?: 768 | 1023;
};

const OCULTAR = { ate768: styles.ate768, ate1023: styles.ate1023, acima768: styles.acima768, acima1023: styles.acima1023 } as const;

const IdiomasMenu: React.FC<PropsIdiomasMenu> = ({
    titulo = 'Idioma',
    classeBloco,
    classeTitulo,
    estiloTitulo,
    classeOpcao,
    classeOpcaoAtiva,
    vertical = false,
    estilo,
    aoEscolher,
    ocultarAte,
    ocultarAcima,
}) => {
    const atual = useIdiomaSite();
    const idTitulo = `idm-${useId().replace(/:/g, '')}`;
    return (
        <div
            className={cx(styles.bloco, classeBloco, ocultarAte && OCULTAR[`ate${ocultarAte}`], ocultarAcima && OCULTAR[`acima${ocultarAcima}`])}
            style={estilo}
            data-idiomas-menu=""
        >
            {titulo ? (
                <span id={idTitulo} className={classeTitulo ?? styles.titulo} style={estiloTitulo}>
                    {titulo}
                </span>
            ) : null}
            <div
                role="group"
                aria-labelledby={titulo ? idTitulo : undefined}
                aria-label={titulo ? undefined : 'Idioma'}
                className={cx(styles.lista, vertical && styles.vertical)}
            >
                {IDIOMAS_SITE.map((lang) => {
                    const ativa = lang === atual;
                    return (
                        <button
                            key={lang}
                            type="button"
                            lang={lang}
                            translate="no"
                            data-idioma={lang}
                            aria-pressed={ativa}
                            className={cx(
                                'notranslate',
                                classeOpcao ? cx(styles.opcaoModelo, classeOpcao) : styles.opcao,
                                ativa && (classeOpcaoAtiva ?? (classeOpcao ? styles.ativaModelo : styles.ativa))
                            )}
                            onClick={() => {
                                trocarIdioma(lang);
                                aoEscolher?.();
                            }}
                        >
                            {/* eslint-disable-next-line @next/next/no-img-element -- bandeira SVG de 20px do CDN do widget */}
                            <img className={styles.bandeira} src={BANDEIRA_IDIOMA[lang]} alt="" width={20} height={20} loading="lazy" decoding="async" />
                            <span>{NOME_IDIOMA[lang]}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default IdiomasMenu;
